// src/pages/Home.js - Fixed Version with Like Functionality
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Code, Cloud, Shield, Cpu, Brain, Database, 
  Blocks, Gamepad2, Zap, Rocket, ChevronRight, 
  Star, MessageCircle, X, Send, CheckCircle, ArrowRight,
  TrendingUp, Users, Clock, Award, Sparkles, Globe,
  Smartphone, Monitor, Tablet, Search, Menu, X as XIcon,
  MessageSquare, ThumbsUp, ExternalLink, Share, Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ============================================================================
// Animated Number Counter Component
// ============================================================================
const AnimatedNumber = ({ target, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);

  useEffect(() => {
    let animationFrame;
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(target * easeOut);
      
      setCount(currentCount);
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [target, duration]);

  return (
    <span className="inline-block">
      {count}{suffix}
    </span>
  );
};

// ============================================================================
// Feedback Carousel Card Component (FIXED)
// ============================================================================
const FeedbackCarouselCard = ({ feedback, onSelectFeedback, onLikeUpdate }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(feedback.user_liked || false);
  const [likeCount, setLikeCount] = useState(feedback.likes_count || 0);
  const { user } = useAuth();

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user) {
      alert('Please login to like feedback');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/feedback/${feedback._id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
        setLikeCount(data.likes_count);
        
        // Notify parent component about the like update
        if (onLikeUpdate) {
          onLikeUpdate(feedback._id, data.likes_count, data.liked);
        }
      }
    } catch (error) {
      console.error('Error liking feedback:', error);
    }
  };

  const handleShare = (e) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: `Feedback from ${feedback.name}`,
        text: `"${feedback.message.substring(0, 100)}..."`,
        url: `${window.location.origin}/feedback/${feedback._id}`,
      });
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/feedback/${feedback._id}`);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div
      className="flex-shrink-0 w-full max-w-md mx-auto sm:w-80 md:w-96 
      bg-white dark:bg-gray-700 rounded-xl sm:rounded-2xl 
      p-4 sm:p-6 shadow-lg hover:shadow-2xl transition-all 
      cursor-pointer border border-gray-200 dark:border-gray-600 
      hover:border-blue-400 active:scale-95 group"
      onClick={() => onSelectFeedback(feedback)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-4 h-4 sm:w-5 sm:h-5 ${
                i < feedback.rating
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-300 dark:text-gray-600'
              }`}
            />
          ))}
        </div>
        {feedback.replies && feedback.replies.length > 0 && (
          <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
            <MessageSquare className="w-4 h-4" />
            <span className="text-xs sm:text-sm">{feedback.replies.length}</span>
          </div>
        )}
      </div>

      <p className="
        text-gray-700 dark:text-gray-300 mb-4 
        line-clamp-3 font-medium whitespace-pre-wrap
        text-sm sm:text-base
      ">
        &quot;{feedback.message}&quot;
      </p>

      {feedback.image && (
        <img
          src={feedback.image}
          alt="Feedback"
          className="w-full h-28 sm:h-32 object-cover rounded-lg mb-4"
          loading="lazy"
        />
      )}

      {/* Social Actions */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all ${
              isLiked 
                ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-500'
            }`}
            aria-label="Like this feedback"
          >
            <Heart className={`w-4 h-4 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
            <span className="text-xs font-medium">{likeCount}</span>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onSelectFeedback(feedback);
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/30 transition-all"
            aria-label="View comments"
          >
            <MessageCircle className="w-4 h-4" />
            <span className="text-xs font-medium">{feedback.replies?.length || 0}</span>
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800/30 transition-all"
            aria-label="Share feedback"
          >
            <Share className="w-4 h-4" />
          </button>
        </div>

        <button className="
          text-blue-600 dark:text-blue-400 
          text-xs sm:text-sm hover:underline
          flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity
        ">
          <span>View Details</span>
          <ExternalLink className="w-3 h-3" />
        </button>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-600">
        <div>
          <p className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
            {feedback.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(feedback.createdAt).toLocaleDateString()}
          </p>
        </div>
        {feedback.city && (
          <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-600 rounded-full text-gray-600 dark:text-gray-400">
            {feedback.city}
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Pinned Feedback Card (FIXED)
// ============================================================================
const PinnedFeedbackCard = ({ feedback, isActive, onClick, onLikeUpdate }) => {
  const [isLiked, setIsLiked] = useState(feedback.user_liked || false);
  const [likeCount, setLikeCount] = useState(feedback.likes_count || 0);
  const { user } = useAuth();

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user) {
      alert('Please login to like feedback');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/feedback/${feedback._id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.liked);
        setLikeCount(data.likes_count);
        
        // Notify parent component about the like update
        if (onLikeUpdate) {
          onLikeUpdate(feedback._id, data.likes_count, data.liked);
        }
      }
    } catch (error) {
      console.error('Error liking feedback:', error);
    }
  };

  return (
    <div
      onClick={onClick}
      className={`transform transition-all duration-500 ease-in-out w-full max-w-md mx-auto cursor-pointer ${
        isActive ? 'scale-100 opacity-100' : 'scale-95 opacity-70'
      }`}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl md:rounded-2xl 
                    p-3 sm:p-4 md:p-5 lg:p-6 shadow-lg w-full hover:shadow-xl transition-shadow">
        <div className="flex items-center gap-1 mb-2 sm:mb-3">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i}
              className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                i < feedback.rating 
                  ? 'text-yellow-400 fill-yellow-400' 
                  : 'text-gray-300 dark:text-gray-600'
              }`} 
            />
          ))}
        </div>
        <p className="text-gray-700 dark:text-gray-300 mb-3 sm:mb-4 
                    text-sm sm:text-base md:text-lg line-clamp-3">
          &quot;{feedback.message}&quot;
        </p>
        
        {/* Social Actions for Pinned Feedback */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all text-sm ${
              isLiked 
                ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
            <span>{likeCount}</span>
          </button>
          
          <button
            onClick={onClick}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/30 text-sm"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            <span>{feedback.replies?.length || 0}</span>
          </button>
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
          <div>
            <p className="font-semibold text-gray-900 dark:text-white 
                        text-sm sm:text-base md:text-lg">
              {feedback.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {new Date(feedback.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ThumbsUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Featured
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Service Card Component
// ============================================================================
const ServiceCard = ({ service, index, isSmall = false }) => {
  const IconComponent = service.icon;
  const [isHovered, setIsHovered] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  return (
    <Link 
      to={service.link}
      className={`
        group relative block
        bg-white dark:bg-gray-800 
        rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 
        dark:border-gray-700 hover:border-transparent 
        hover:shadow-2xl transition-all duration-500 
        transform hover:-translate-y-1 sm:hover:-translate-y-2 active:scale-[0.98]
        animate-fade-in overflow-hidden
        ${isSmall ? 'p-4 sm:p-6' : 'p-5 sm:p-7 md:p-8'}
        min-h-[250px] sm:min-h-[300px] md:min-h-[320px]
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
      `}
      style={{ animationDelay: `${index * 0.1}s` }}
      onMouseEnter={() => !isTouch && setIsHovered(true)}
      onMouseLeave={() => !isTouch && setIsHovered(false)}
      onTouchStart={() => {
        setIsHovered(true);
        setIsTouch(true);
      }}
      onTouchEnd={() => setIsHovered(false)}
      aria-label={`Learn more about ${service.title}`}
    >
      {/* Gradient Background Animation */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br ${service.gradient} 
                    opacity-0 group-hover:opacity-10 transition-opacity duration-500
                    ${isHovered ? 'opacity-10' : 'opacity-0'}`}
      />
      
      {/* Decorative Corner */}
      <div 
        className={`absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 
                    ${service.gradient} opacity-0 
                    group-hover:opacity-5 transition-opacity duration-500
                    rounded-bl-full`}
      />
      
      <div className="relative mb-3 sm:mb-4 md:mb-6">
        <div 
          className={`
            inline-flex p-2 sm:p-3 md:p-4 bg-gradient-to-br ${service.gradient} 
            rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg transform 
            transition-all duration-500
            ${isHovered ? 'scale-110 rotate-3 sm:rotate-6' : 'scale-100 rotate-0'}
          `}
        >
          <IconComponent className="
            w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 
            lg:w-10 lg:h-10 xl:w-12 xl:h-12 text-white
          " />
        </div>
      </div>
      
      <h3 
        className={`
          font-bold mb-2 sm:mb-3 text-gray-900 dark:text-white 
          transition-all duration-300
          ${isSmall ? 'text-base sm:text-lg md:text-xl' : 'text-lg sm:text-xl md:text-2xl lg:text-3xl'}
          ${isHovered ? 'scale-105' : 'scale-100'}
          ${service.hoverColor}
        `}
      >
        {service.title}
      </h3>
      
      <p 
        className={`
          text-gray-600 dark:text-gray-300 mb-3 sm:mb-4 md:mb-6
          transition-colors duration-300
          ${isSmall ? 'text-xs sm:text-sm md:text-base' : 'text-sm sm:text-base md:text-lg'}
          line-clamp-3
        `}
      >
        {service.description}
      </p>
      
      <div 
        className={`
          flex items-center font-semibold ${service.hoverColor} 
          transition-all duration-300 mt-auto pt-3 sm:pt-4
          ${isSmall ? 'text-xs sm:text-sm md:text-base' : 'text-sm sm:text-base md:text-lg'}
          ${isHovered ? 'translate-x-2' : 'translate-x-0'}
        `}
      >
        <span className="mr-1 sm:mr-2">Learn More</span>
        <ArrowRight 
          className={`
            w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5
            transition-transform duration-300
            ${isHovered ? 'translate-x-1' : 'translate-x-0'}
          `} 
        />
      </div>
    </Link>
  );
};

// ============================================================================
// Responsive Hero Features Grid
// ============================================================================
const HeroFeatureGrid = () => {
  const features = [
    { icon: Code, title: 'Software Dev', color: 'from-blue-500 to-blue-600' },
    { icon: Cloud, title: 'Cloud Solutions', color: 'from-purple-500 to-purple-600' },
    { icon: Shield, title: 'Cyber Security', color: 'from-green-500 to-green-600' },
    { icon: Database, title: 'Data Analytics', color: 'from-orange-500 to-orange-600' }
  ];

  return (
    <div className="relative z-10 w-full max-w-2xl mx-auto lg:max-w-none">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
        {features.map((item, index) => {
          const Icon = item.icon;
          return (
            <div
              key={index}
              className="
                bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm
                p-3 sm:p-4 md:p-5 lg:p-6 rounded-lg sm:rounded-xl md:rounded-2xl 
                shadow-lg hover:shadow-2xl transition-all duration-300 
                transform hover:scale-105 active:scale-95
                animate-float border border-gray-200/50 dark:border-gray-700/50
              "
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div 
                className={`
                  inline-flex p-2 sm:p-3 md:p-4 bg-gradient-to-br ${item.color} 
                  rounded-lg sm:rounded-xl mb-2 sm:mb-3 md:mb-4
                `}
              >
                <Icon className="
                  w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 
                  lg:w-7 lg:h-7 text-white
                " />
              </div>
              <h3 className="
                text-xs sm:text-sm md:text-base lg:text-lg 
                font-bold text-gray-900 dark:text-white
              ">
                {item.title}
              </h3>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// Responsive Stats Grid
// ============================================================================
const StatsGrid = () => {
  const stats = [
    { value: 50, label: 'Projects Delivered', suffix: '+', icon: Award },
    { value: 15, label: 'Technology Experts', suffix: '+', icon: Users },
    { value: 250, label: 'Happy Clients', suffix: '+', icon: TrendingUp },
    { value: 24, label: 'Support Hours', suffix: '/7', icon: Clock }
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div 
            key={index}
            className="
              text-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm
              p-3 sm:p-4 md:p-5 lg:p-6 rounded-lg sm:rounded-xl md:rounded-2xl shadow-lg
              transform hover:scale-105 transition-all duration-300
              animate-fade-in border border-gray-200/50 dark:border-gray-700/50
            "
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="inline-flex p-1.5 sm:p-2 md:p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mb-2 sm:mb-3">
              <Icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </div>
            <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-blue-600 dark:text-blue-400 mb-1">
              <AnimatedNumber target={stat.value} suffix={stat.suffix} />
            </div>
            <div className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
              {stat.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Main Home Component (FIXED)
// ============================================================================
const Home = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [carouselPaused, setCarouselPaused] = useState(false);
  const { user } = useAuth();
  const [pinnedFeedbacks, setPinnedFeedbacks] = useState([]);
  const [pinnedIndex, setPinnedIndex] = useState(0);
  const [pinnedPauseAutoRotate, setPinnedPauseAutoRotate] = useState(false);
  const pinnedAutoRotateRef = useRef(null);
  const [currentBreakpoint, setCurrentBreakpoint] = useState('');

  // Function to update like status across all feedback arrays
  const updateFeedbackLikeStatus = (feedbackId, newLikesCount, newLikedStatus) => {
    // Update main feedbacks
    setFeedbacks(prev => prev.map(feedback => 
      feedback._id === feedbackId 
        ? { ...feedback, likes_count: newLikesCount, user_liked: newLikedStatus }
        : feedback
    ));

    // Update pinned feedbacks
    setPinnedFeedbacks(prev => prev.map(feedback => 
      feedback._id === feedbackId 
        ? { ...feedback, likes_count: newLikesCount, user_liked: newLikedStatus }
        : feedback
    ));

    // Update selected feedback if it's the same one
    if (selectedFeedback && selectedFeedback._id === feedbackId) {
      setSelectedFeedback(prev => ({
        ...prev,
        likes_count: newLikesCount,
        user_liked: newLikedStatus
      }));
    }
  };

  // Detect current breakpoint for debugging
  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 640) setCurrentBreakpoint('sm');
      else if (width < 768) setCurrentBreakpoint('md');
      else if (width < 1024) setCurrentBreakpoint('lg');
      else if (width < 1280) setCurrentBreakpoint('xl');
      else setCurrentBreakpoint('2xl');
    };

    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);

  // Main Services (Memoized)
  const mainServices = useMemo(() => [
    {
      icon: Code,
      title: 'Software Development',
      description: 'Custom software solutions tailored to your business needs with modern tech stacks',
      link: '/services/software-development',
      gradient: 'from-blue-500 to-blue-600',
      hoverColor: 'group-hover:text-blue-600'
    },
    {
      icon: Cloud,
      title: 'Cloud Computing',
      description: 'Scalable cloud infrastructure and migration services for optimal performance',
      link: '/services/cloud-computing',
      gradient: 'from-cyan-500 to-cyan-600',
      hoverColor: 'group-hover:text-cyan-600'
    },
    {
      icon: Shield,
      title: 'Cyber Security',
      description: 'Comprehensive security solutions to protect your digital assets',
      link: '/services/cyber-security',
      gradient: 'from-red-500 to-red-600',
      hoverColor: 'group-hover:text-red-600'
    },
    {
      icon: Cpu,
      title: 'VLSI Design',
      description: 'Advanced VLSI and embedded systems design with industry expertise',
      link: '/services/vlsi-design',
      gradient: 'from-purple-500 to-purple-600',
      hoverColor: 'group-hover:text-purple-600'
    },
    {
      icon: Brain,
      title: 'AI/ML Solutions',
      description: 'Intelligent automation and machine learning models for business growth',
      link: '/services/ai-ml-solutions',
      gradient: 'from-pink-500 to-pink-600',
      hoverColor: 'group-hover:text-pink-600'
    },
    {
      icon: Database,
      title: 'Data Analytics',
      description: 'Transform raw data into actionable business insights and strategies',
      link: '/services/data-analytics',
      gradient: 'from-green-500 to-green-600',
      hoverColor: 'group-hover:text-green-600'
    }
  ], []);

  // Additional Services (Memoized)
  const additionalServices = useMemo(() => [
    {
      icon: Blocks,
      title: 'Blockchain & Web3',
      description: 'Decentralized solutions and smart contracts',
      link: '/services/blockchain',
      gradient: 'from-amber-500 to-amber-600',
      hoverColor: 'group-hover:text-amber-600'
    },
    {
      icon: Gamepad2,
      title: 'Game Development',
      description: 'Engaging games across multiple platforms',
      link: '/services/game-development',
      gradient: 'from-rose-500 to-rose-600',
      hoverColor: 'group-hover:text-rose-600'
    },
    {
      icon: Zap,
      title: 'AI Automation',
      description: 'Streamline processes with intelligent automation',
      link: '/services/ai-automation',
      gradient: 'from-yellow-500 to-yellow-600',
      hoverColor: 'group-hover:text-yellow-600'
    },
    {
      icon: Rocket,
      title: 'Startup MVP',
      description: 'Rapid MVP development for startups',
      link: '/services/startup-mvp',
      gradient: 'from-teal-500 to-teal-600',
      hoverColor: 'group-hover:text-teal-600'
    },
    {
      icon: Cpu,
      title: 'VLSI Services',
      description: 'Comprehensive semiconductor design services',
      link: '/services/vlsi',
      gradient: 'from-violet-500 to-violet-600',
      hoverColor: 'group-hover:text-violet-600'
    }
  ], []);

  // Fetch Feedbacks
  useEffect(() => {
    const fetchFeedbacks = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {};
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_URL}/api/feedback`, { headers });
        
        if (response.ok) {
          const data = await response.json();
          console.log('📥 Fetched feedbacks:', data.feedbacks?.length || 0);
          
          // Log first feedback to debug
          if (data.feedbacks && data.feedbacks.length > 0) {
            console.log('🔍 Sample feedback:', {
              id: data.feedbacks[0]._id,
              likes_count: data.feedbacks[0].likes_count,
              user_liked: data.feedbacks[0].user_liked
            });
          }
          
          setFeedbacks(data.feedbacks || []);
        } else {
          console.error('❌ Failed to fetch feedbacks:', response.status);
        }
      } catch (error) {
        console.error('❌ Error fetching feedbacks:', error);
      }
    };

    fetchFeedbacks();
    const feedbackInterval = setInterval(fetchFeedbacks, 30000);
    return () => clearInterval(feedbackInterval);
  }, []);

  useEffect(() => {
    const fetchPinned = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {};
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_URL}/api/feedback/pinned`, { headers });
        
        if (response.ok) {
          const data = await response.json();
          console.log('📌 Fetched pinned feedbacks:', data.pinnedFeedbacks?.length || 0);
          setPinnedFeedbacks(data.pinnedFeedbacks || []);
          setPinnedIndex(0);
        }
      } catch (error) {
        console.error('Error fetching pinned feedbacks:', error);
      }
    };
    fetchPinned();
  }, []);

  // Auto-rotate pinned feedbacks
  useEffect(() => {
    if (pinnedFeedbacks.length <= 1 || pinnedPauseAutoRotate) return;

    pinnedAutoRotateRef.current = setInterval(() => {
      setPinnedIndex((prev) => (prev === pinnedFeedbacks.length - 1 ? 0 : prev + 1));
    }, 5000);

    return () => {
      if (pinnedAutoRotateRef.current) clearInterval(pinnedAutoRotateRef.current);
    };
  }, [pinnedFeedbacks.length, pinnedPauseAutoRotate]);

  const handlePinnedPrev = () => {
    setPinnedIndex((prev) => (prev === 0 ? pinnedFeedbacks.length - 1 : prev - 1));
    setPinnedPauseAutoRotate(true);
    setTimeout(() => setPinnedPauseAutoRotate(false), 500);
  };

  const handlePinnedNext = () => {
    setPinnedIndex((prev) => (prev === pinnedFeedbacks.length - 1 ? 0 : prev + 1));
    setPinnedPauseAutoRotate(true);
    setTimeout(() => setPinnedPauseAutoRotate(false), 500);
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim()) {
      alert('Please enter a reply message');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/feedback/${selectedFeedback._id}/reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
          body: JSON.stringify({ message: replyText }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        alert(data.error || 'Failed to post reply');
        return;
      }

      setReplyText('');
      const feedbacksResponse = await fetch(`${API_URL}/api/feedback`);
      if (feedbacksResponse.ok) {
        const listData = await feedbacksResponse.json();
        setFeedbacks(listData.feedbacks || []);
        const updated = listData.feedbacks.find(f => f._id === selectedFeedback._id);
        if (updated) setSelectedFeedback(updated);
      }
    } catch (error) {
      console.error('Error posting reply:', error);
      alert('Failed to post reply. Please try again.');
    }
  };

  const handleCarouselMouseEnter = () => setCarouselPaused(true);
  const handleCarouselMouseLeave = () => setCarouselPaused(false);
  const handleCarouselTouchStart = () => setCarouselPaused(true);
  const handleCarouselTouchEnd = () => setCarouselPaused(false);

  // Modal like handler
  const handleModalLike = async () => {
    if (!user) {
      alert('Please login to like feedback');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/feedback/${selectedFeedback._id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        updateFeedbackLikeStatus(selectedFeedback._id, data.likes_count, data.liked);
      }
    } catch (error) {
      console.error('Error liking feedback:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 overflow-x-hidden">
      {/* SECTION 1: Hero Section - Fully Responsive */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-14 sm:pt-16 lg:pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 
                        dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20">
          <div className="absolute top-10 left-2 sm:top-20 sm:left-10 w-32 h-32 sm:w-48 sm:h-48 md:w-72 md:h-72 bg-blue-400 
                          rounded-full opacity-20 blur-xl sm:blur-2xl md:blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-2 sm:bottom-20 sm:right-10 w-40 h-40 sm:w-64 sm:h-64 md:w-96 md:h-96 
                          bg-purple-400 rounded-full opacity-20 blur-xl sm:blur-2xl md:blur-3xl animate-pulse" 
                style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 md:py-12 lg:py-16 xl:py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 xl:gap-16 items-center">
            
            {/* Left Column - Content */}
            <div className="text-center lg:text-left space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
              <div className="inline-flex items-center px-2.5 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 
                              bg-blue-100 dark:bg-blue-900/30 rounded-full 
                              text-blue-600 dark:text-blue-400 font-semibold text-xs sm:text-sm md:text-base">
                <Rocket className="mr-1.5 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4" />
                Leading Technology Solutions Provider
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl 
                           font-bold leading-tight">
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 
                                bg-clip-text text-transparent block">
                  Transform Your Business
                </span>
                <span className="text-gray-900 dark:text-white block mt-1 sm:mt-2">
                  with Technology
                </span>
              </h1>

              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 dark:text-gray-300 
                          max-w-2xl mx-auto lg:mx-0">
                We deliver cutting-edge solutions in Software Development, Cloud Computing, 
                VLSI Design, AI/ML, and more. Your success is our mission.
              </p>

              {/* Responsive Button Group */}
              <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 md:gap-4 justify-center lg:justify-start">
                <Link
                  to="/contact"
                  className="
                    group px-5 sm:px-6 md:px-7 lg:px-8 
                    py-2.5 sm:py-3 md:py-3.5 lg:py-4
                    bg-gradient-to-r from-blue-600 to-purple-600 
                    text-white rounded-full font-semibold text-sm sm:text-base md:text-lg 
                    shadow-lg hover:shadow-2xl transform hover:scale-105 
                    active:scale-95 transition-all duration-300 
                    flex items-center justify-center min-h-[44px] sm:min-h-[48px] md:min-h-[52px] lg:min-h-[56px]
                  "
                >
                  <span>Get Started</span>
                  <ChevronRight className="ml-1.5 sm:ml-2 w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5
                                          group-hover:translate-x-1 sm:group-hover:translate-x-2 transition-transform" />
                </Link>
                <Link
                  to="/services"
                  className="
                    px-5 sm:px-6 md:px-7 lg:px-8 
                    py-2.5 sm:py-3 md:py-3.5 lg:py-4
                    bg-white dark:bg-gray-800 text-gray-900 dark:text-white 
                    rounded-full font-semibold text-sm sm:text-base md:text-lg 
                    border-2 border-gray-300 dark:border-gray-600 
                    hover:border-blue-600 dark:hover:border-blue-400 
                    transform hover:scale-105 active:scale-95 
                    transition-all duration-300 
                    flex items-center justify-center min-h-[44px] sm:min-h-[48px] md:min-h-[52px] lg:min-h-[56px]
                  "
                >
                  Explore Services
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 lg:gap-6 
                            justify-center lg:justify-start items-center 
                            text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                <div className="flex items-center">
                  <CheckCircle className="text-green-500 mr-1.5 sm:mr-2 
                                         w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                  <span className="whitespace-nowrap">Startup India Recognised</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="text-green-500 mr-1.5 sm:mr-2 
                                         w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                  <span className="whitespace-nowrap">50+ Projects</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="text-green-500 mr-1.5 sm:mr-2 
                                         w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                  <span className="whitespace-nowrap">24/7 Support</span>
                </div>
              </div>
            </div>

            {/* Right Column - Features Grid */}
            <div className="lg:block animate-slide-in-right">
              <HeroFeatureGrid />
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 hidden lg:block">
          <div className="animate-bounce">
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 rotate-90" />
          </div>
        </div>
      </section>

      {/* SECTION 2: Responsive Stats Section */}
      <section className="relative py-8 sm:py-12 md:py-16 lg:py-20 bg-white dark:bg-gray-900 z-10">
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          <div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12 text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 md:mb-4">
              <span className="text-gray-900 dark:text-white">Our Impact in </span>
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 
                             bg-clip-text text-transparent">Numbers</span>
            </h2>
            <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 dark:text-gray-400 
                        max-w-2xl mx-auto">
              Delivering excellence across industries with measurable results
            </p>
          </div>
          
          <StatsGrid />
        </div>
      </section>

      {/* SECTION 3: Main Services Section */}
      <section className="relative py-8 sm:py-12 md:py-16 lg:py-20 
                          bg-gradient-to-br from-gray-50 to-gray-100 
                          dark:from-gray-800 dark:to-gray-900 z-10">
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          <div className="text-center mb-8 sm:mb-10 md:mb-12 lg:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 sm:mb-3 md:mb-4">
              <span className="text-gray-900 dark:text-white">Our </span>
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 
                             bg-clip-text text-transparent">Core Services</span>
            </h2>
            <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-gray-600 dark:text-gray-400 
                        max-w-3xl mx-auto">
              Comprehensive technology solutions tailored to your business needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8">
            {mainServices.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: Additional Services - Responsive Grid */}
      <section className="relative py-8 sm:py-12 md:py-16 lg:py-20 bg-white dark:bg-gray-900 z-10">
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          <div className="text-center mb-8 sm:mb-10 md:mb-12 lg:mb-16">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 
                         font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 md:mb-4">
              Specialized Services
            </h2>
            <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl 
                        text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Explore our additional cutting-edge technology offerings
            </p>
          </div>
          
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 
                        gap-2.5 sm:gap-3 md:gap-4 lg:gap-5 xl:gap-6">
            {additionalServices.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} isSmall={true} />
            ))}
          </div>
          
          <div className="text-center mt-6 sm:mt-8 md:mt-10 lg:mt-12">
            <Link 
              to="/services" 
              className="
                inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 
                text-white px-5 sm:px-6 md:px-7 lg:px-8 
                py-2.5 sm:py-3 md:py-3.5 lg:py-4
                rounded-full font-semibold text-sm sm:text-base md:text-lg 
                shadow-lg hover:shadow-2xl 
                transform hover:scale-105 active:scale-95 transition-all duration-300
                min-h-[44px] sm:min-h-[48px] md:min-h-[52px] lg:min-h-[56px]
              "
            >
              <span>View All Services</span>
              <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ml-1.5 sm:ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {pinnedFeedbacks.length > 0 && (
      <section className="relative py-4 sm:py-6 md:py-8 lg:py-10 xl:py-12 
                        bg-gradient-to-r from-blue-50 to-purple-50 
                        dark:from-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 max-w-4xl">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <div className="h-0.5 sm:h-1 w-4 sm:w-6 md:w-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded"></div>
            <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-center 
                        text-gray-900 dark:text-white">
              ⭐ Featured Feedback
            </h3>
            <div className="h-0.5 sm:h-1 w-4 sm:w-6 md:w-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded"></div>
          </div>

          <div 
            className="relative rounded-lg sm:rounded-xl md:rounded-2xl 
                      bg-white dark:bg-gray-700 p-3 sm:p-4 md:p-5 lg:p-6 
                      shadow-lg sm:shadow-xl md:shadow-2xl border-2 border-blue-200 dark:border-blue-900 
                      overflow-hidden"
            onMouseEnter={() => setPinnedPauseAutoRotate(true)}
            onMouseLeave={() => setPinnedPauseAutoRotate(false)}
          >
            <div className="relative z-10">
              <div className="flex-1 flex justify-center">
                <PinnedFeedbackCard
                  key={pinnedFeedbacks[pinnedIndex]?._id}
                  feedback={pinnedFeedbacks[pinnedIndex]}
                  isActive={true}
                  onClick={() => setSelectedFeedback(pinnedFeedbacks[pinnedIndex])}
                  onLikeUpdate={updateFeedbackLikeStatus}
                />
              </div>

              {/* Progress indicator */}
              <div className="flex justify-center items-center gap-2 mt-3 sm:mt-4 mb-2 sm:mb-3">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                  {pinnedIndex + 1} / {pinnedFeedbacks.length}
                </span>
                <div className="flex gap-1 sm:gap-1.5">
                  {pinnedFeedbacks.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setPinnedIndex(idx);
                        setPinnedPauseAutoRotate(true);
                        setTimeout(() => setPinnedPauseAutoRotate(false), 500);
                      }}
                      className={`
                        h-1.5 sm:h-2 rounded-full transition-all duration-300 cursor-pointer
                        ${idx === pinnedIndex
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 w-4 sm:w-5 md:w-6'
                          : 'bg-gray-300 dark:bg-gray-600 w-1.5 sm:w-2 hover:bg-gray-400'
                        }
                      `}
                      aria-label={`Go to feedback ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            {pinnedFeedbacks.length > 1 && (
              <>
                <button
                  onClick={handlePinnedPrev}
                  aria-label="Previous feedback"
                  className="
                    absolute left-1 sm:left-2 md:left-3 top-1/2 -translate-y-1/2 z-20 
                    rounded-full bg-blue-600 text-white 
                    w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12
                    flex items-center justify-center 
                    hover:bg-blue-700 hover:scale-110 active:scale-95
                    shadow-md sm:shadow-lg transition-all duration-200 
                    font-bold text-xs sm:text-sm md:text-lg hover:shadow-xl
                  "
                >
                  ‹
                </button>

                <button
                  onClick={handlePinnedNext}
                  aria-label="Next feedback"
                  className="
                    absolute right-1 sm:right-2 md:right-3 top-1/2 -translate-y-1/2 z-20 
                    rounded-full bg-blue-600 text-white 
                    w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-12 lg:h-12
                    flex items-center justify-center 
                    hover:bg-blue-700 hover:scale-110 active:scale-95
                    shadow-md sm:shadow-lg transition-all duration-200 
                    font-bold text-xs sm:text-sm md:text-lg hover:shadow-xl
                  "
                >
                  ›
                </button>
              </>
            )}
          </div>
          
          <div className="text-center mt-3 sm:mt-4">
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Click on any feedback to view details, like, comment, or share
            </p>
          </div>
        </div>
      </section>
    )}

      {/* SECTION 5: ANIMATED CAROUSEL FEEDBACK */}
      {feedbacks.length > 0 && (
        <section className="relative py-6 sm:py-8 md:py-10 lg:py-12 
                          bg-gradient-to-br from-gray-50 to-gray-100 
                          dark:from-gray-800 dark:to-gray-900 z-10">
          <div className="container mx-auto px-3 sm:px-4 md:px-6">
            <div className="text-center mb-4 sm:mb-6 md:mb-8">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-1.5 sm:mb-2 md:mb-3">
                <span className="text-gray-900 dark:text-white">What Our </span>
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Clients Say
                </span>
              </h2>
              <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 dark:text-gray-400">
                Real-time feedback from our satisfied customers ({feedbacks.length} reviews)
              </p>
            </div>

            <div className="relative mb-3 sm:mb-4 md:mb-6">
              <div
                className="relative overflow-hidden bg-white dark:bg-gray-800 
                          rounded-lg sm:rounded-xl md:rounded-2xl 
                          p-2.5 sm:p-3 md:p-4 lg:p-6 
                          shadow-lg sm:shadow-xl md:shadow-2xl 
                          border border-gray-200 dark:border-gray-700"
                onMouseEnter={handleCarouselMouseEnter}
                onMouseLeave={handleCarouselMouseLeave}
                onTouchStart={handleCarouselTouchStart}
                onTouchEnd={handleCarouselTouchEnd}
              >
                <div className="relative overflow-hidden">
                  <div
                    className="carousel-track-home flex gap-3 sm:gap-4 md:gap-6"
                    style={{
                      animation: 'carousel-scroll 7s linear infinite',
                      animationPlayState: carouselPaused ? 'paused' : 'running',
                    }}
                  >
                    {feedbacks.map((feedback) => (
                      <FeedbackCarouselCard
                        key={`original-${feedback._id}`}
                        feedback={feedback}
                        onSelectFeedback={() => setSelectedFeedback(feedback)}
                        onLikeUpdate={updateFeedbackLikeStatus}
                      />
                    ))}

                    {feedbacks.length > 1 &&
                      feedbacks.map((feedback) => (
                        <FeedbackCarouselCard
                          key={`duplicate-${feedback._id}`}
                          feedback={feedback}
                          onSelectFeedback={() => setSelectedFeedback(feedback)}
                          onLikeUpdate={updateFeedbackLikeStatus}
                        />
                      ))}
                  </div>
                </div>

                {/* Side fades */}
                <div className="absolute left-0 top-0 bottom-0 w-4 sm:w-8 md:w-12 
                              bg-gradient-to-r from-white dark:from-gray-800 to-transparent 
                              pointer-events-none z-10"></div>
                <div className="absolute right-0 top-0 bottom-0 w-4 sm:w-8 md:w-12 
                              bg-gradient-to-l from-white dark:from-gray-800 to-transparent 
                              pointer-events-none z-10"></div>
              </div>
            </div>

            <div className="text-center">
              <Link
                to="/feedback"
                className="
                  inline-block bg-blue-600 text-white 
                  px-4 sm:px-5 md:px-6 py-1.5 sm:py-2 md:py-2.5
                  rounded-lg sm:rounded-xl hover:bg-blue-700 
                  transition shadow-md sm:shadow-lg 
                  font-semibold text-xs sm:text-sm md:text-base
                  min-h-[36px] sm:min-h-[40px] md:min-h-[44px]
                  flex items-center justify-center
                "
              >
                View All {feedbacks.length} Reviews
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 6: CTA Section - Responsive */}
      <section className="relative py-8 sm:py-12 md:py-16 lg:py-20 
                        bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 z-10">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 
                       font-bold text-white mb-3 sm:mb-4 md:mb-5 lg:mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-blue-100 
                     mb-4 sm:mb-5 md:mb-6 lg:mb-7 xl:mb-8 
                     max-w-2xl mx-auto">
            Let's discuss how we can help you achieve your goals
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 md:gap-4 justify-center">
            <Link
              to="/contact"
              className="
                px-4 sm:px-5 md:px-6 lg:px-7 xl:px-8 
                py-2 sm:py-2.5 md:py-3 lg:py-3.5 xl:py-4
                bg-white text-blue-600 
                rounded-full font-semibold text-sm sm:text-base md:text-lg 
                shadow-lg hover:shadow-2xl transform hover:scale-105 
                active:scale-95 transition-all duration-300
                min-h-[40px] sm:min-h-[44px] md:min-h-[48px] lg:min-h-[52px] xl:min-h-[56px]
                flex items-center justify-center
              "
            >
              Contact Us Today
            </Link>
            <Link
              to="/portfolio"
              className="
                px-4 sm:px-5 md:px-6 lg:px-7 xl:px-8 
                py-2 sm:py-2.5 md:py-3 lg:py-3.5 xl:py-4
                bg-transparent border-2 border-white 
                text-white rounded-full font-semibold text-sm sm:text-base md:text-lg 
                hover:bg-white hover:text-blue-600 transition-all duration-300
                min-h-[40px] sm:min-h-[44px] md:min-h-[48px] lg:min-h-[52px] xl:min-h-[56px]
                flex items-center justify-center
              "
            >
              View Our Work
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================================== */}
      {/* FEEDBACK DETAILS MODAL (FIXED) */}
      {/* =========================================================== */}
      {selectedFeedback && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
          onClick={() => setSelectedFeedback(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-3xl w-full my-8 shadow-2xl border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
            {/* Header with social actions */}
            <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 z-10 rounded-t-2xl">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Feedback Details</h3>
              <div className="flex items-center gap-2">
                {/* Like Button */}
                <button
                  onClick={handleModalLike}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                >
                  <Heart className={`w-5 h-5 ${selectedFeedback.user_liked ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
                  <span className="text-sm font-medium">{selectedFeedback.likes_count || 0}</span>
                </button>
                
                {/* Share Button */}
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: `Feedback from ${selectedFeedback.name}`,
                        text: `"${selectedFeedback.message.substring(0, 100)}..."`,
                        url: `${window.location.origin}/feedback/${selectedFeedback._id}`,
                      });
                    } else {
                      navigator.clipboard.writeText(`${window.location.origin}/feedback/${selectedFeedback._id}`);
                      alert('Link copied to clipboard!');
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                >
                  <Share className="w-5 h-5 text-gray-500" />
                </button>
                
                <button
                  onClick={() => setSelectedFeedback(null)}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  aria-label="Close modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] space-y-6">
              {/* Rating & User Info */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg flex-shrink-0">
                    {selectedFeedback.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-xl text-gray-900 dark:text-white">{selectedFeedback.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{selectedFeedback.email}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedFeedback.city && selectedFeedback.state
                        ? `${selectedFeedback.city}, ${selectedFeedback.state}`
                        : selectedFeedback.city || 'India'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{new Date(selectedFeedback.createdAt).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-7 h-7 ${i < selectedFeedback.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
              </div>

              {/* Message */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed text-lg">
                  &quot;{selectedFeedback.message}&quot;
                </p>
              </div>

              {/* Image */}
              {selectedFeedback.image && (
                <img
                  src={selectedFeedback.image}
                  alt="Feedback"
                  className="w-full rounded-xl shadow-lg"
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
              )}

              {/* Replies Section */}
              <div className="border-t dark:border-gray-700 pt-6">
                <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <MessageCircle className="w-6 h-6 text-blue-600" />
                  Replies ({selectedFeedback.replies?.length || 0})
                </h4>
                {/* Existing Replies */}
                {selectedFeedback.replies && selectedFeedback.replies.length > 0 ? (
                  <div className="space-y-4 mb-6 max-h-60 overflow-y-auto">
                    {selectedFeedback.replies.map((reply, idx) => (
                      <div key={idx} className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 rounded-lg p-4 border border-blue-100 dark:border-gray-600">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white">{reply.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(reply.createdAt).toLocaleString()}</p>
                          </div>
                          {/* Edit/Delete buttons for reply owner */}
                          {user && (user.id === reply.userId || user.role === 'admin') && (
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  const newMessage = prompt('Edit your reply:', reply.message);
                                  if (newMessage && newMessage.trim()) {
                                    try {
                                      const response = await fetch(
                                        `${API_URL}/api/feedback/${selectedFeedback._id}/reply/${reply._id}`,
                                        {
                                          method: 'PATCH',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                          },
                                          body: JSON.stringify({ message: newMessage }),
                                        }
                                      );
                                      if (response.ok) {
                                        // Refresh feedback
                                        const updatedResponse = await fetch(`${API_URL}/api/feedback/${selectedFeedback._id}`);
                                        if (updatedResponse.ok) {
                                          const data = await updatedResponse.json();
                                          setSelectedFeedback(data.feedback);
                                        }
                                      }
                                    } catch (error) {
                                      console.error('Error editing reply:', error);
                                    }
                                  }
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Edit
                              </button>
                              <button
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to delete this reply?')) {
                                    try {
                                      const response = await fetch(
                                        `${API_URL}/api/feedback/${selectedFeedback._id}/reply/${reply._id}`,
                                        {
                                          method: 'DELETE',
                                          headers: {
                                            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                          },
                                        }
                                      );
                                      if (response.ok) {
                                        // Refresh feedback
                                        const updatedResponse = await fetch(`${API_URL}/api/feedback/${selectedFeedback._id}`);
                                        if (updatedResponse.ok) {
                                          const data = await updatedResponse.json();
                                          setSelectedFeedback(data.feedback);
                                        }
                                      }
                                    } catch (error) {
                                      console.error('Error deleting reply:', error);
                                    }
                                  }
                                }}
                                className="text-xs text-red-600 hover:text-red-800"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-center text-gray-600 dark:text-gray-400">
                    <p className="text-sm">No replies yet. Be the first to respond!</p>
                  </div>
                )}

                {/* Add Reply Form */}
                <div className="space-y-4 bg-gray-50 dark:bg-gray-700 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                  <h5 className="font-bold text-lg text-gray-900 dark:text-white">Add Your Reply</h5>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write your reply..."
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white resize-none"
                    aria-label="Reply message"
                  />
                  <button
                    onClick={handleReplySubmit}
                    className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition shadow-lg font-semibold text-lg"
                  >
                    <Send className="w-5 h-5" />
                    <span>Post Reply</span>
                  </button>
                </div>
              </div>

              {/* Edit/Delete Feedback for Owner */}
              {user && (user.id === selectedFeedback.userId || user.role === 'admin') && (
                <div className="border-t dark:border-gray-700 pt-6">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Manage Feedback</h4>
                  <div className="flex gap-3">
                    <button
                      onClick={async () => {
                        const newMessage = prompt('Edit your feedback:', selectedFeedback.message);
                        if (newMessage && newMessage.trim()) {
                          try {
                            const response = await fetch(
                              `${API_URL}/api/feedback/my/${selectedFeedback._id}`,
                              {
                                method: 'PATCH',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                },
                                body: JSON.stringify({ message: newMessage }),
                              }
                            );
                            if (response.ok) {
                              const data = await response.json();
                              setSelectedFeedback(data.feedback);
                              alert('Feedback updated successfully!');
                            }
                          } catch (error) {
                            console.error('Error editing feedback:', error);
                          }
                        }
                      }}
                      className="flex-1 bg-yellow-500 text-white px-4 py-3 rounded-lg hover:bg-yellow-600 transition font-semibold"
                    >
                      Edit Feedback
                    </button>
                    <button
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
                          try {
                            const response = await fetch(
                              `${API_URL}/api/feedback/my/${selectedFeedback._id}`,
                              {
                                method: 'DELETE',
                                headers: {
                                  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                },
                              }
                            );
                            if (response.ok) {
                              setSelectedFeedback(null);
                              alert('Feedback deleted successfully!');
                              // Refresh feedback list
                              const feedbacksResponse = await fetch(`${API_URL}/api/feedback`);
                              if (feedbacksResponse.ok) {
                                const listData = await feedbacksResponse.json();
                                setFeedbacks(listData.feedbacks || []);
                              }
                            }
                          } catch (error) {
                            console.error('Error deleting feedback:', error);
                          }
                        }
                      }}
                      className="flex-1 bg-red-500 text-white px-4 py-3 rounded-lg hover:bg-red-600 transition font-semibold"
                    >
                      Delete Feedback
                    </button>
                  </div>
                </div>
              )}

              {/* Add social actions section */}
              <div className="border-t dark:border-gray-700 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">Social Actions</h4>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleModalLike}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                      selectedFeedback.user_liked
                        ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${selectedFeedback.user_liked ? 'fill-red-500 text-red-500' : ''}`} />
                    <span className="font-medium">Like</span>
                    <span className="text-sm bg-white dark:bg-gray-800 px-2 py-0.5 rounded-full">
                      {selectedFeedback.likes_count || 0}
                    </span>
                  </button>
                  
                  <button
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: `Feedback from ${selectedFeedback.name}`,
                          text: `"${selectedFeedback.message.substring(0, 100)}..."`,
                          url: `${window.location.origin}/feedback/${selectedFeedback._id}`,
                        });
                      } else {
                        navigator.clipboard.writeText(`${window.location.origin}/feedback/${selectedFeedback._id}`);
                        alert('Link copied to clipboard!');
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-800/30 transition"
                  >
                    <Share className="w-5 h-5" />
                    <span className="font-medium">Share</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug Breakpoint Indicator (Remove in production) */}
      <div className="fixed bottom-3 sm:bottom-4 right-3 sm:right-4 z-50 bg-black/80 text-white 
                    px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-xs font-mono hidden">
        Breakpoint: {currentBreakpoint}
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        /* Custom breakpoint for very small devices */
        @media (min-width: 375px) and (max-width: 475px) {
          .xs\\:grid-cols-2 {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes carousel-scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-in-right {
          animation: slideInRight 0.6s ease-out forwards;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-scale-in {
          animation: scaleIn 0.25s ease-out forwards;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .animate-slide-in-right {
            animation: fadeIn 0.5s ease-out forwards;
          }
        }

        /* Touch device optimizations */
        @media (hover: none) and (pointer: coarse) {
          .hover\\:scale-105:hover {
            transform: scale(1);
          }
        }

        /* Reduced motion preferences */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in,
          .animate-slide-in-right,
          .animate-float,
          .animate-scale-in,
          .transform,
          .transition-all,
          .carousel-track-home {
            animation: none !important;
            transition: none !important;
          }
        }

        /* Dark mode optimizations */
        @media (prefers-color-scheme: dark) {
          .bg-gradient-to-br {
            background-blend-mode: multiply;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;